import posthog from "posthog-js";
import { POSTHOG_KEY } from "../../env";
import { getTenantId } from "../../services/utils";
import { PostHogSurveyAPIResponse, SurveyConfig, SurveyResponses } from "./survey-types";
import { PRAXIS_PLAY_EVENTS } from "./events";

const POSTHOG_API_HOST = "https://us.i.posthog.com";

export async function fetchSurveyConfig(): Promise<SurveyConfig | null> {
  try {
    const response = await fetch(`${POSTHOG_API_HOST}/api/surveys/?token=${POSTHOG_KEY}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      console.warn("[Survey] Failed to fetch survey config:", response.status);
      return null;
    }

    const data: PostHogSurveyAPIResponse = await response.json();

    if (!Array.isArray(data?.surveys)) {
      console.warn("[Survey] Unexpected API response shape:", data);
      return null;
    }

    const tenant = getTenantId();

    // Priority 1: tenant-specific survey (name contains tenant)
    let survey = data.surveys.find((s) => s.name.includes(tenant));

    // Priority 2: default survey (name without " - " tenant suffix)
    if (!survey) {
      survey = data.surveys.find((s) => s.name.includes("CSAT") && !s.name.includes(" - "));
    }

    if (!survey || survey.questions.length < 3) {
      console.warn("[Survey] No valid survey found or insufficient questions");
      return null;
    }

    return {
      surveyId: survey.id,
      surveyName: survey.name,
      ratingQuestionId: survey.questions[0].id,
      positiveOptionsQuestionId: survey.questions[1].id,
      negativeOptionsQuestionId: survey.questions[2].id,
    };
  } catch (error) {
    console.error("[Survey] Error fetching survey config:", error);
    return null;
  }
}

export function sendSurveyShown(config: SurveyConfig, sessionId: string): void {
  posthog.capture(PRAXIS_PLAY_EVENTS.POSTCALL_SURVEY_SHOWN, {
    $survey_id: config.surveyId,
    $survey_name: config.surveyName,
    session_id: sessionId,
    tenant_id: getTenantId(),
  });
}

export function sendSurveySent(
  config: SurveyConfig,
  sessionId: string,
  timeSpentSeconds: number,
  responses: SurveyResponses
): void {
  posthog.capture(PRAXIS_PLAY_EVENTS.POSTCALL_SURVEY_SENT, {
    $survey_id: config.surveyId,
    $survey_name: config.surveyName,
    session_id: sessionId,
    time_spent_seconds: timeSpentSeconds,
    tenant_id: getTenantId(),
    ...responses,
  });
}

export function sendSurveyDismissed(
  config: SurveyConfig,
  sessionId: string,
  timeSpentSeconds: number
): void {
  posthog.capture(PRAXIS_PLAY_EVENTS.POSTCALL_SURVEY_DISMISSED, {
    $survey_id: config.surveyId,
    $survey_name: config.surveyName,
    $survey_response: "dismissed",
    session_id: sessionId,
    time_spent_seconds: timeSpentSeconds,
    tenant_id: getTenantId(),
  });
}
